import { createFrame, updateFrame } from "@/api/spaces";
import { loadDocHighlights } from "@/components/shapes/doc-frame/doc-editor.lazy";
import type { HighlightSource } from "@/components/shapes/doc-frame/doc-highlights";
import { useCanvasStore } from "@/stores/canvas-store";
import { translate } from "@/i18n";
import { canvasMessages } from "@/i18n/messages/canvas";
import { useFramesStore } from "@/stores/frames-store";
import type { FrameData } from "@/lib/frame-data";
import { ensureFrameDetail } from "@/lib/frame-detail-requests";
import { applyFramePatch, ingestFrame } from "@/lib/frame-ingest";

function frameById(frameId: string): FrameData | undefined {
  return useFramesStore.getState().framesById[frameId];
}

export function flashHighlightOverlay(rect: DOMRect) {
  const overlay = document.createElement("div");
  overlay.style.position = "fixed";
  overlay.style.left = `${rect.left}px`;
  overlay.style.top = `${rect.top}px`;
  overlay.style.width = `${rect.width}px`;
  overlay.style.height = `${rect.height}px`;
  overlay.style.backgroundColor =
    "color-mix(in srgb, var(--color-warning) 40%, transparent)";
  overlay.style.borderRadius = "var(--appkit-radius-control-sm)";
  overlay.style.pointerEvents = "none";
  overlay.style.zIndex = "9999";
  overlay.style.transition = "opacity 0.8s ease-out";
  document.body.appendChild(overlay);
  requestAnimationFrame(() => {
    setTimeout(() => {
      overlay.style.opacity = "0";
      setTimeout(() => overlay.remove(), 800);
    }, 400);
  });
}

export function findHighlightsFrame(): { id: string } | null {
  const { framesById, frameOrder } = useFramesStore.getState();
  const id = frameOrder.find(
    (candidate) => framesById[candidate]?.metadata?.isHighlightsFrame === true,
  );
  return id ? { id } : null;
}

/**
 * Save the source frame's highlighted texts. Its whole metadata is saved,
 * so it is read from the full frame: a card-only frame's summary subset
 * would erase the keys it leaves out. A failed load has shown its toast.
 */
async function changeSourceHighlights(
  projectId: string,
  frameId: string,
  change: (highlightedTexts: string[]) => string[],
) {
  const source = await ensureFrameDetail(frameId).catch(() => null);
  if (!source) return;
  const previous =
    (source.metadata?.highlightedTexts as string[] | undefined) ?? [];
  const metadata = { ...source.metadata, highlightedTexts: change(previous) };
  await updateFrame(projectId, frameId, { metadata });
  applyFramePatch(frameId, { metadata });
}

export function addSourceHighlightText(
  projectId: string,
  frameId: string,
  selectedText: string,
) {
  return changeSourceHighlights(projectId, frameId, (texts) => [
    ...texts,
    selectedText,
  ]);
}

async function saveHighlightsContent(
  projectId: string,
  frameId: string,
  content: Record<string, unknown>,
) {
  await updateFrame(projectId, frameId, { content });
  applyFramePatch(frameId, { content });
}

export async function removeHighlight(
  projectId: string,
  frameId: string,
  selectedText: string,
) {
  await changeSourceHighlights(projectId, frameId, (texts) =>
    texts.filter((text) => text !== selectedText),
  );

  const existing = findHighlightsFrame();
  if (!existing) return;
  // Entries are removed from the full content; a failed load has shown its toast.
  const data = await ensureFrameDetail(existing.id).catch(() => null);
  if (!data) return;

  const { removeDocHighlight } = await loadDocHighlights();
  const content = removeDocHighlight(
    data.frameType,
    data.content ?? {},
    selectedText,
  );
  if (content) await saveHighlightsContent(projectId, existing.id, content);
}

async function appendToHighlightsFrame(
  projectId: string,
  frameId: string,
  selectedText: string,
  source: HighlightSource,
) {
  let frame = frameById(frameId);
  if (frame) {
    // Entries append to the full content; a failed load has shown its toast.
    frame = await ensureFrameDetail(frameId).catch(() => undefined);
    if (!frame) return;
  }
  const { appendDocHighlight } = await loadDocHighlights();
  const content = appendDocHighlight(
    frame?.frameType ?? "doc",
    frame?.content ?? {},
    selectedText,
    source,
  );
  await saveHighlightsContent(projectId, frameId, content);
}

export async function appendHighlight(
  projectId: string,
  existingFrameId: string | null,
  selectedText: string,
  sourceFrameId: string,
  sourceFrameTitle: string,
  sourceFrameType: string,
): Promise<void> {
  const source = {
    id: sourceFrameId,
    title: sourceFrameTitle,
    frameType: sourceFrameType,
  };
  if (existingFrameId) {
    await appendToHighlightsFrame(
      projectId,
      existingFrameId,
      selectedText,
      source,
    );
    return;
  }

  const { appendDocHighlight } = await loadDocHighlights();
  const content = appendDocHighlight("doc", {}, selectedText, source);
  const metadata = { isHighlightsFrame: true };
  const frame = await createFrame(projectId, {
    frame_type: "doc",
    title: translate(canvasMessages, "highlightsFrameTitle"),
    content,
    width: 400,
    height: 300,
  });
  await updateFrame(projectId, frame.id, { metadata });
  ingestFrame({ ...frame, content, metadata });
  requestAnimationFrame(() => {
    useCanvasStore.getState().navigateToFrame(frame.id);
  });
}
